// src/components/CustomPlayer/CustomPlayer.tsx
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { TrackEntry } from '@/types/streaming';
import { SeekBar } from './SeekBar';
import { useFullscreen } from './useFullscreen';
import { useHold2x } from './useHold2x';
import { SettingsPanel, type PlaybackSpeed } from './SettingsPanel';
import type { DownloadState } from './useStreamDownload';
import './CustomPlayer.css';

// ─────────── Control glyphs ───────────
// Inline SVGs instead of emoji so the chrome doesn't pick up the user's
// system emoji font + colors (the play/mute/settings emoji rendered as
// large color glyphs on iOS Safari, breaking the dark monochrome chrome).
// 18 × 18 viewBox keeps them aligned with the 16-px button font-size.

const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 18 18',
  fill: 'currentColor',
  'aria-hidden': true,
} as const;

function IconPlay(): JSX.Element {
  return <svg {...ICON_PROPS}><path d="M4 3v12l11-6L4 3z" /></svg>;
}
function IconPause(): JSX.Element {
  return <svg {...ICON_PROPS}><path d="M4 3h4v12H4zM10 3h4v12h-4z" /></svg>;
}
function IconVolume(): JSX.Element {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 7v4h3l4 3V4L6 7H3z" />
      <path d="M12.5 6.5a3.5 3.5 0 0 1 0 5L11.4 10.4a2 2 0 0 0 0-2.8z" />
    </svg>
  );
}
function IconMuted(): JSX.Element {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 7v4h3l4 3V4L6 7H3z" />
      <path d="M13 7l3 3M16 7l-3 3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </svg>
  );
}
function IconSettings(): JSX.Element {
  return (
    <svg {...ICON_PROPS}>
      <path d="M9 6.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zm6.4 2.5l1.4-1L15.5 5l-1.6.6a4.5 4.5 0 0 0-1.4-.8L12 3H6l-.5 1.8a4.5 4.5 0 0 0-1.4.8L2.5 5 1.2 8l1.4 1A4.4 4.4 0 0 0 2.5 9c0 .3 0 .6.1.9L1.2 11l1.3 3 1.6-.6a4.5 4.5 0 0 0 1.4.8L6 16h6l.5-1.8a4.5 4.5 0 0 0 1.4-.8l1.6.6 1.3-3-1.4-1c0-.3.1-.6.1-.9s0-.6-.1-.9z" />
    </svg>
  );
}
function IconFullscreen(): JSX.Element {
  return (
    <svg {...ICON_PROPS} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3,7 3,3 7,3" />
      <polyline points="11,3 15,3 15,7" />
      <polyline points="15,11 15,15 11,15" />
      <polyline points="7,15 3,15 3,11" />
    </svg>
  );
}
function IconExitFullscreen(): JSX.Element {
  return (
    <svg {...ICON_PROPS} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="7,3 7,7 3,7" />
      <polyline points="15,7 11,7 11,3" />
      <polyline points="11,15 11,11 15,11" />
      <polyline points="3,11 7,11 7,15" />
    </svg>
  );
}
function IconDownload(): JSX.Element {
  return (
    <svg {...ICON_PROPS} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3v8" />
      <polyline points="5,8 9,12 13,8" />
      <path d="M3 15h12" />
    </svg>
  );
}
function IconCheck(): JSX.Element {
  return (
    <svg {...ICON_PROPS} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3,9 7,13 15,5" />
    </svg>
  );
}
function IconClose(): JSX.Element {
  return (
    <svg {...ICON_PROPS} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M5 5l8 8M13 5l-8 8" />
    </svg>
  );
}

export interface CustomPlayerProps {
  videoRef: RefObject<HTMLVideoElement>;
  fatal: string | null;
  onRetry: () => void;
  autoMuted: boolean;
  onUnmute: () => void;
  tracks: TrackEntry[];
  osBlobTrack: { lang: string; url: string } | null;
  topOverlay?: ReactNode;
  settingsPanels?: {
    quality?: ReactNode;
    source?: ReactNode;
    subtitles?: ReactNode;
    buffer?: ReactNode;
  };
  /**
   * Optional client-side download handle. When provided, a download button is
   * shown in the bottom bar reflecting `state`; clicking starts the download
   * (idle/done/error) or cancels it (preparing/downloading). Pass null/omit to
   * hide the button entirely (e.g. live or DRM streams, or the progressive-MP4
   * path where there is no hls.js instance to stitch from).
   */
  download?: {
    state: DownloadState;
    onStart: () => void;
    onCancel: () => void;
  } | null;
  disableTopOverlay?: boolean;
  title?: string;
}

/** Bottom-bar button reflecting the client-side download state machine. */
function DownloadButton(
  { state, onStart, onCancel, t }: NonNullable<CustomPlayerProps['download']> & { t: TFunction },
): JSX.Element {
  const inProgress = state.phase === 'preparing' || state.phase === 'downloading';
  const label =
    state.phase === 'downloading' ? t('player.download.cancelPct', { pct: state.pct })
    : state.phase === 'preparing' ? t('player.download.preparing')
    : state.phase === 'done' ? t('player.download.done')
    : state.phase === 'error' ? t('player.download.failed')
    : t('player.download.download');
  const className = [
    'player-root__icon-btn',
    'player-root__download',
    `player-root__download--${state.phase}`,
  ].join(' ');
  return (
    <button
      type="button"
      className={className}
      data-testid="player-download"
      aria-label={label}
      title={state.phase === 'error' ? t('player.download.failedTitle') : label}
      onClick={inProgress ? onCancel : onStart}
    >
      {state.phase === 'downloading' ? (
        <span className="player-root__download-pct">{state.pct}%</span>
      ) : state.phase === 'preparing' ? (
        <span className="player-root__download-spinner" aria-hidden="true" />
      ) : state.phase === 'done' ? (
        <IconCheck />
      ) : state.phase === 'error' ? (
        <IconClose />
      ) : (
        <IconDownload />
      )}
    </button>
  );
}

const SKIP_FWD_S = 10;
const SKIP_BACK_S = 5;
const VOLUME_STORAGE_KEY = 'cobstream:player:volume';

function readPersistedVolume(): number | null {
  try {
    const raw = window.localStorage.getItem(VOLUME_STORAGE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || n > 1) return null;
    return n;
  } catch { return null; }
}

function writePersistedVolume(v: number): void {
  try { window.localStorage.setItem(VOLUME_STORAGE_KEY, String(v)); } catch { /* quota */ }
}

function clampedSeek(video: HTMLVideoElement, delta: number): void {
  const next = Math.max(0, Math.min(video.duration || Number.MAX_VALUE, video.currentTime + delta));
  video.currentTime = next;
}

function fmt(t: number): string {
  if (!Number.isFinite(t)) return '0:00';
  const s = Math.max(0, Math.floor(t));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  const hh = Math.floor(mm / 60);
  if (hh > 0) return `${hh}:${String(mm % 60).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

export function CustomPlayer(props: CustomPlayerProps): JSX.Element {
  const {
    videoRef, fatal, onRetry, autoMuted, onUnmute,
    tracks, osBlobTrack, topOverlay, disableTopOverlay, title,
  } = props;
  const { t } = useTranslation();

  const rootRef = useRef<HTMLDivElement>(null);
  // Pass the videoRef so fullscreen can fall back to the <video>'s native
  // webkitEnterFullscreen on iOS (where Element.requestFullscreen is absent).
  const fullscreen = useFullscreen(rootRef, videoRef);
  const hold2x = useHold2x(videoRef, { thresholdMs: 500, moveCancelPx: 8 });
  const { active: hold2xActive, handlers: hold2xHandlers } = hold2x;
  const [paused, setPaused] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState<{ start: number; end: number }[]>([]);
  // True while the element is seeking or starved of data. Surfaced as a
  // spinner so a seek that takes a few seconds (iOS native HLS downloads and
  // decodes a whole segment before showing the target frame) reads as
  // "working", not as a frozen player.
  const [buffering, setBuffering] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const on = (): void => setBuffering(true);
    const off = (): void => setBuffering(false);
    v.addEventListener('seeking', on);
    v.addEventListener('waiting', on);
    v.addEventListener('playing', off);
    v.addEventListener('canplay', off);
    v.addEventListener('emptied', off);
    return (): void => {
      v.removeEventListener('seeking', on);
      v.removeEventListener('waiting', on);
      v.removeEventListener('playing', off);
      v.removeEventListener('canplay', off);
      v.removeEventListener('emptied', off);
    };
  }, [videoRef]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    function refresh(): void {
      if (!v) return;
      setPaused(v.paused);
      setCurrentTime(v.currentTime);
      setDuration(Number.isFinite(v.duration) ? v.duration : 0);
    }
    refresh();
    v.addEventListener('play', refresh);
    v.addEventListener('pause', refresh);
    v.addEventListener('timeupdate', refresh);
    v.addEventListener('durationchange', refresh);
    return (): void => {
      v.removeEventListener('play', refresh);
      v.removeEventListener('pause', refresh);
      v.removeEventListener('timeupdate', refresh);
      v.removeEventListener('durationchange', refresh);
    };
  }, [videoRef]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    function refreshBuffered(): void {
      if (!v) return;
      const ranges: { start: number; end: number }[] = [];
      for (let i = 0; i < v.buffered.length; i += 1) {
        ranges.push({ start: v.buffered.start(i), end: v.buffered.end(i) });
      }
      setBuffered(ranges);
    }
    v.addEventListener('progress', refreshBuffered);
    v.addEventListener('timeupdate', refreshBuffered);
    return (): void => {
      v.removeEventListener('progress', refreshBuffered);
      v.removeEventListener('timeupdate', refreshBuffered);
    };
  }, [videoRef]);

  const onSeekTo = useCallback((t: number): void => {
    const v = videoRef.current;
    if (v) v.currentTime = t;
  }, [videoRef]);

  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  // Apply persisted volume on first mount of <video>.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const persisted = readPersistedVolume();
    if (persisted != null) {
      v.volume = persisted;
      setVolume(persisted);
    } else {
      setVolume(v.volume);
    }
  }, [videoRef]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    function refresh(): void {
      if (!v) return;
      setMuted(v.muted);
      setVolume(v.volume);
    }
    v.addEventListener('volumechange', refresh);
    return (): void => v.removeEventListener('volumechange', refresh);
  }, [videoRef]);

  const toggleMute = useCallback((): void => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
  }, [videoRef]);

  const setVideoVolume = useCallback((next: number): void => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = next;
    writePersistedVolume(next);
  }, [videoRef]);

  const lastTapLeftRef = useRef(0);
  const lastTapRightRef = useRef(0);
  const DOUBLE_TAP_WINDOW_MS = 300;

  const onDoubleTapLeft = useCallback((): void => {
    const now = Date.now();
    if (now - lastTapLeftRef.current <= DOUBLE_TAP_WINDOW_MS) {
      const v = videoRef.current;
      if (v) clampedSeek(v, -SKIP_BACK_S);
      lastTapLeftRef.current = 0;
    } else {
      lastTapLeftRef.current = now;
    }
  }, [videoRef]);

  const onDoubleTapRight = useCallback((): void => {
    const now = Date.now();
    if (now - lastTapRightRef.current <= DOUBLE_TAP_WINDOW_MS) {
      const v = videoRef.current;
      if (v) clampedSeek(v, SKIP_FWD_S);
      lastTapRightRef.current = 0;
    } else {
      lastTapRightRef.current = now;
    }
  }, [videoRef]);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!settingsOpen) return undefined;
    function onPointerDown(e: globalThis.PointerEvent): void {
      const target = e.target as Node;
      if (settingsRef.current && !settingsRef.current.contains(target)) {
        setSettingsOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return (): void => document.removeEventListener('pointerdown', onPointerDown);
  }, [settingsOpen]);

  const applySpeed = useCallback((s: PlaybackSpeed): void => {
    const v = videoRef.current;
    if (v) v.playbackRate = s;
  }, [videoRef]);

  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    function onRateChange(): void { if (v) setPlaybackRate(v.playbackRate); }
    setPlaybackRate(v.playbackRate); // initial sync
    v.addEventListener('ratechange', onRateChange);
    return (): void => v.removeEventListener('ratechange', onRateChange);
  }, [videoRef]);

  const [idle, setIdle] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const IDLE_MS = 2_000;

  // Refs let resetIdle stay stable across renders while still reading the
  // latest gating flags (settingsOpen, hold2xActive) when the timer fires.
  const settingsOpenRef = useRef(settingsOpen);
  settingsOpenRef.current = settingsOpen;
  const hold2xActiveRef = useRef(hold2xActive);
  hold2xActiveRef.current = hold2xActive;

  const resetIdle = useCallback((): void => {
    setIdle(false);
    if (idleTimerRef.current != null) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      if (settingsOpenRef.current) return;
      if (hold2xActiveRef.current) return;
      const v = videoRef.current;
      if (v && !v.paused) setIdle(true);
    }, IDLE_MS);
  }, [videoRef]);

  useEffect(() => {
    resetIdle();
    return (): void => {
      if (idleTimerRef.current != null) clearTimeout(idleTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    function onPause(): void { setIdle(false); }
    v.addEventListener('pause', onPause);
    v.addEventListener('play', resetIdle);
    return (): void => {
      v.removeEventListener('pause', onPause);
      v.removeEventListener('play', resetIdle);
    };
  }, [videoRef, resetIdle]);

  const togglePlay = useCallback((): void => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void v.play();
    else v.pause();
  }, [videoRef]);

  const lastMidTapRef = useRef<number>(0);
  const MID_TAP_GAP_MS = 250;

  const onMidZoneClick = useCallback((): void => {
    const now = Date.now();
    if (now - lastMidTapRef.current < MID_TAP_GAP_MS) return;
    lastMidTapRef.current = now;
    togglePlay();
  }, [togglePlay]);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>): void => {
    // Don't steal keys from text inputs (LobbyChat etc.).
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
    const v = videoRef.current;
    if (!v) return;
    if (e.key === ' ') {
      e.preventDefault();
      togglePlay();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      clampedSeek(v, SKIP_FWD_S);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      clampedSeek(v, -SKIP_BACK_S);
    } else if (e.key === 'm' || e.key === 'M') {
      e.preventDefault();
      toggleMute();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setVideoVolume(Math.min(1, (videoRef.current?.volume ?? 1) + 0.05));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setVideoVolume(Math.max(0, (videoRef.current?.volume ?? 0) - 0.05));
    } else if (e.key === 'f' || e.key === 'F') {
      e.preventDefault();
      void fullscreen.toggle();
    }
  }, [togglePlay, videoRef, toggleMute, setVideoVolume, fullscreen]);

  return (
    <div
      ref={rootRef}
      className={[
        'player-root',
        fullscreen.isFullscreen ? 'player-root--is-fullscreen' : '',
        idle ? 'player-root--idle' : '',
      ].filter(Boolean).join(' ')}
      data-testid="player-root"
      aria-label={title}
      tabIndex={0}
      onPointerMove={resetIdle}
      onKeyDown={(e): void => { resetIdle(); onKeyDown(e); }}
    >
      <video
        ref={videoRef}
        className="player-root__video"
        playsInline
        crossOrigin="anonymous"
        controlsList="nodownload nofullscreen noremoteplayback"
        disablePictureInPicture
      >
        {/* id = URL: TextTrack.id mirrors the element id, letting the Watch
            page target the exact selected track — language is ambiguous
            (providers ship duplicate langs, plus the OpenSubtitles track). */}
        {tracks.map((t) => (
          <track key={t.url} id={t.url} kind={t.kind} srcLang={t.lang} label={t.label} src={t.url} />
        ))}
        {osBlobTrack && (
          <track
            key={osBlobTrack.url}
            id={osBlobTrack.url}
            kind="subtitles"
            srcLang={osBlobTrack.lang}
            label={t('player.osTrackLabel', { lang: osBlobTrack.lang.toUpperCase() })}
            src={osBlobTrack.url}
            default
          />
        )}
      </video>

      <div className="player-root__zones" aria-hidden="true">
        <div
          className="player-root__zone player-root__zone--left"
          data-testid="player-left-zone"
          onClick={onDoubleTapLeft}
        />
        <div
          className="player-root__zone player-root__zone--mid"
          data-testid="player-mid-zone"
          onClick={onMidZoneClick}
          onPointerDown={hold2xHandlers.onPointerDown}
          onPointerMove={hold2xHandlers.onPointerMove}
          onPointerUp={hold2xHandlers.onPointerUp}
          onPointerCancel={hold2xHandlers.onPointerCancel}
        />
        <div
          className="player-root__zone player-root__zone--right"
          data-testid="player-right-zone"
          onClick={onDoubleTapRight}
        />
      </div>
      {buffering && !fatal && (
        <div className="player-root__buffering" data-testid="player-buffering" role="status" aria-label={t('player.buffering')}>
          <span className="player-root__buffering-spinner" aria-hidden="true" />
        </div>
      )}
      {hold2xActive && (
        <div className="player-root__speed-pill" data-testid="player-speed-pill" role="status">
          <span>2×</span>
          <IconPlay />
        </div>
      )}

      {!disableTopOverlay && topOverlay != null && (
        <div className="player-root__top" data-testid="player-top">
          {topOverlay}
        </div>
      )}

      <div className="player-root__bottom">
        <button
          type="button"
          className="player-root__icon-btn"
          data-testid="player-play"
          aria-label={paused ? t('player.play') : t('player.pause')}
          onClick={togglePlay}
        >
          {paused ? <IconPlay /> : <IconPause />}
        </button>
        <SeekBar
          currentTime={currentTime}
          duration={duration}
          buffered={buffered}
          onSeek={onSeekTo}
        />
        <span className="player-root__time" data-testid="player-time">
          {fmt(currentTime)} / {fmt(duration)}
        </span>
        <button
          type="button"
          className="player-root__icon-btn"
          data-testid="player-mute"
          aria-label={muted ? t('player.unmute') : t('player.mute')}
          onClick={toggleMute}
        >
          {muted ? <IconMuted /> : <IconVolume />}
        </button>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={volume}
          data-testid="player-volume"
          aria-label={t('player.volume')}
          className="player-root__volume"
          onChange={(e): void => setVideoVolume(Number(e.target.value))}
        />
        <button
          type="button"
          className="player-root__icon-btn"
          data-testid="player-settings"
          aria-label={t('player.settings')}
          onClick={(): void => setSettingsOpen((o) => !o)}
        >
          <IconSettings />
        </button>
        {props.download && (
          <DownloadButton
            state={props.download.state}
            onStart={props.download.onStart}
            onCancel={props.download.onCancel}
            t={t}
          />
        )}
        <button
          type="button"
          className="player-root__icon-btn"
          data-testid="player-fullscreen"
          aria-label={fullscreen.isFullscreen ? t('player.exitFullscreen') : t('player.enterFullscreen')}
          onClick={(): void => { void fullscreen.toggle(); }}
        >
          {fullscreen.isFullscreen ? <IconExitFullscreen /> : <IconFullscreen />}
        </button>
      </div>

      {settingsOpen && (
        <div ref={settingsRef} className="player-root__settings-popover">
          <SettingsPanel
            speed={playbackRate}
            onSpeedChange={applySpeed}
            slots={props.settingsPanels ?? {}}
            onClose={(): void => setSettingsOpen(false)}
          />
        </div>
      )}

      {fatal != null && (
        <div
          className="player-root__fatal"
          role="alert"
          data-testid="player-fatal"
          onClick={onRetry}
        >
          <div>{t('player.streamUnavailable', { fatal })}</div>
          <button type="button" onClick={onRetry}>{t('common.retry')}</button>
        </div>
      )}
      {autoMuted && (
        <button
          type="button"
          className="player-root__unmute"
          data-testid="player-unmute"
          onClick={onUnmute}
        >
          {t('player.tapToUnmute')}
        </button>
      )}
    </div>
  );
}
